import React from 'react';
import { useTenant } from '../context/TenantContext';
import { Link } from 'react-router-dom';

export const TermsOfService: React.FC = () => {
  const { tenant } = useTenant();
  const orgName = tenant?.name || 'Your Organisation';
  const lastUpdated = 'March 2026';

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
      <p className="text-sm text-gray-500 mb-10">Last updated: {lastUpdated}</p>

      <div className="prose prose-gray max-w-none space-y-8">
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h2>
          <p className="text-gray-700 leading-relaxed">
            By accessing or using the KulaLearn platform (&ldquo;the Platform&rdquo;),
            you agree to be bound by these Terms of Service. The Platform is operated by
            ThinkShow (Pty) Ltd (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;)
            on behalf of {orgName} (&ldquo;the Organisation&rdquo;). If you do not agree
            to these terms, please do not use the Platform.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Service Description</h2>
          <p className="text-gray-700 leading-relaxed">
            KulaLearn is a learning management platform that enables organisations to
            deliver online courses, track learner progress, and manage educational content.
            The Platform is provided to you through your Organisation&rsquo;s subscription.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">3. User Accounts</h2>
          <p className="text-gray-700 leading-relaxed">
            Accounts are created and managed by your Organisation&rsquo;s administrators.
            You are responsible for maintaining the confidentiality of your login
            credentials. You must notify your Organisation administrator immediately if you
            become aware of any unauthorised use of your account.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Acceptable Use</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            When using the Platform, you agree not to:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-1">
            <li>Share your login credentials with others</li>
            <li>Attempt to access courses or data you are not authorised to view</li>
            <li>Upload harmful, offensive, or illegal content</li>
            <li>Interfere with the Platform&rsquo;s operation or security</li>
            <li>Use automated tools to scrape or extract content from the Platform</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Intellectual Property</h2>
          <p className="text-gray-700 leading-relaxed">
            Course content, materials, and assessments are the intellectual property of
            {' '}{orgName} or their respective content creators. The Platform software is the
            intellectual property of ThinkShow (Pty) Ltd. You may not reproduce,
            distribute, or create derivative works from any content on the Platform without
            prior written consent.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Data Protection</h2>
          <p className="text-gray-700 leading-relaxed">
            Your use of the Platform is subject to our{' '}
            <Link to="/privacy" className="text-blue-600 hover:underline">
              Privacy Policy
            </Link>
            , which explains what personal information we collect, how we use it, and your
            rights under the Protection of Personal Information Act (POPIA). By using the
            Platform, you acknowledge that you have read and understood our Privacy Policy.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Third-Party Services</h2>
          <p className="text-gray-700 leading-relaxed">
            The Platform may include links to or integrations with third-party services
            (such as embedded videos or external learning tools). We are not responsible
            for the content, privacy practices, or terms of any third-party services. Your
            use of such services is at your own risk.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Certificates</h2>
          <p className="text-gray-700 leading-relaxed">
            Completion certificates are generated by the Platform based on your course
            progress as recorded by the system. Certificates are issued for informational
            purposes and do not constitute formal accreditation unless expressly stated by
            the Organisation.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Limitation of Liability</h2>
          <p className="text-gray-700 leading-relaxed">
            The Platform is provided &ldquo;as is&rdquo; without warranty of any kind. To
            the maximum extent permitted by law, ThinkShow (Pty) Ltd shall not be liable
            for any indirect, incidental, or consequential damages arising from your use
            of the Platform, including but not limited to loss of data, interruption of
            service, or errors in course content.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Governing Law</h2>
          <p className="text-gray-700 leading-relaxed">
            These Terms are governed by the laws of the Republic of South Africa,
            including the Protection of Personal Information Act (POPIA), the Consumer
            Protection Act, and the Electronic Communications and Transactions Act. Any
            disputes shall be subject to the jurisdiction of the South African courts.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Changes to These Terms</h2>
          <p className="text-gray-700 leading-relaxed">
            We may update these Terms from time to time. Material changes will be
            communicated through the Platform or via your Organisation. Continued use of
            the Platform after changes are posted constitutes acceptance of the revised
            Terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Contact</h2>
          <p className="text-gray-700 leading-relaxed">
            For questions about these Terms, please contact your Organisation
            administrator ({orgName}) or ThinkShow (Pty) Ltd at{' '}
            <a href="mailto:hello@thinkshow.co" className="text-blue-600 hover:underline">
              hello@thinkshow.co
            </a>.
          </p>
        </section>
      </div>
    </div>
  );
};
